import { connect, ConnectedProps } from "react-redux";
import { withRouter } from "react-router";
import { RouteComponentProps } from "react-router-dom";
import { CallState, DateRange } from "redmond";
import { Dispatch } from "@reduxjs/toolkit";
import * as H from "history";
import { isCorpTenant } from "@capone/common";
import { config } from "../../../../api/config";
import { IStoreState } from "../../../../reducers/types";

import {
  fetchTravelWalletDetails,
  IFetchTravelOffersDetails,
} from "../../../travel-wallet/actions/actions";
import { FlightList } from "./component";
import {
  fetchPackagesFlightShop,
  fetchPackagesTripDetails,
  IFetchPackagesFlightShop,
  IFetchPackagesTripDetails,
  ISetPackagesChosenOutgoingSlice,
  ISetPackagesChosenReturnSlice,
  setAirlineFilter,
  setAirportFilter,
  setDurationFilter,
  setFlightNumberFilter,
  setMaxPriceFilter,
  setOutboundArrivalTimeRange,
  setOutboundDepartureTimeRange,
  setPackagesChosenOutgoingSlice,
  setPackagesChosenReturnSlice,
  setReturnArrivalTimeRange,
  setReturnDepartureTimeRange,
} from "../../actions/actions";
import {
  getRecommendedFlights,
  getPackageNightCountBasedOnAvailability,
} from "../../../availability/reducer";
import {
  getAirShopSummary,
  getFareTripDetailsById,
  getFlightShopCallResults,
  flightsSelector,
  getIsFlightShopLoading,
  getIsOutgoing,
  getIsTripDetailsLoading,
  getOutboundFlightsToRender,
  getPackagesByReturnFareSlice,
  getParsedPackagesByOutboundFareSlice,
  getRecommendedOutgoingFlight,
  getRecommendedReturnFlight,
  getReturnFlightsToRender,
  getSelectedTrip,
  getSortedAndFilteredFlights,
  getMaxPriceFilter,
  getFlightShopCallState,
  getAirportMap,
  hasSetNonFareclassFiltersSelector,
  initialFilterOptions,
  getViewedFlightListProperties,
  getSelectedReturnSliceProperties,
  getSelectedOutgoingSliceProperties,
  getIsReturn,
  getPackageNightCountBasedOnOutboundFlight,
} from "../../reducer";
import {
  getSelectedAccount,
  getSelectedAccountReferenceIdIfRedemptionEnabled,
  getUserIsPrimaryCardHolder,
} from "../../../rewards/reducer";
import {
  getDepartureDate,
  getDestination,
  getOrigin,
  getReturnDate,
} from "../../../search/reducer";

import { setStopsOption } from "../../../search/actions/actions";
import { setDateRange } from "../../../hotel-shop/actions/actions";
import { getHotelShopDateRange } from "../../../hotel-shop/reducer";

const mapStateToProps = (state: IStoreState) => ({
  recommendedFlights: getRecommendedFlights(state),
  flightShopCallResults: getFlightShopCallResults(state),
  isFlightShopLoading: getIsFlightShopLoading(state),
  rewardsKey:
    getSelectedAccountReferenceIdIfRedemptionEnabled(state) || undefined,
  airShopSummary: getAirShopSummary(state),
  flights: flightsSelector(state),
  packageNightCountBasedOnAvailability:
    getPackageNightCountBasedOnAvailability(state),
  packageNightCountBasedOnOutboundFlight:
    getPackageNightCountBasedOnOutboundFlight(state),
  packagesByOutboundFareSlice: getParsedPackagesByOutboundFareSlice(state),
  packagesByReturnFareSlice: getPackagesByReturnFareSlice(state),
  origin: getOrigin(state),
  destination: getDestination(state),
  isTripDetailsLoading: getIsTripDetailsLoading(state),
  tripDetailsById: getFareTripDetailsById(state),
  isOutgoing: getIsOutgoing(state),
  isReturn: getIsReturn(state),
  outboundFlights: getOutboundFlightsToRender(state),
  returnFlights: getReturnFlightsToRender(state),
  selectedTrip: getSelectedTrip(state),
  sortedFlights: getSortedAndFilteredFlights(state),
  maxPriceFilter: getMaxPriceFilter(state),
  outgoingRecommendedFlight: getRecommendedOutgoingFlight(state),
  returnRecommendedFlight: getRecommendedReturnFlight(state),
  hasFlightShopCallFailed: getFlightShopCallState(state) === CallState.Failed,
  isFlightShopCallNotCalled:
    getFlightShopCallState(state) === CallState.NotCalled,
  departureDate: getDepartureDate(state),
  returnDate: getReturnDate(state),
  airports: getAirportMap(state),
  hasAppliedNonFareclassFilter: hasSetNonFareclassFiltersSelector(state),
  viewedFlightListProperties: getViewedFlightListProperties(state),
  selectedReturnSliceProperties: getSelectedReturnSliceProperties(state),
  selectedOutgoingSliceProperties: getSelectedOutgoingSliceProperties(state),
  hotelShopDateRange: getHotelShopDateRange(state),
  selectedRewardsAccount: getSelectedAccount(state),
  canEarnRewards:
    !isCorpTenant(config.TENANT) || getUserIsPrimaryCardHolder(state),
});

interface IDispatchProps {
  fetchTravelWalletDetails: () => IFetchTravelOffersDetails;
  fetchPackagesFlightShop: (
    history: H.History,
    options?: {
      overrideStateByQueryParams?: boolean;
      forceCallPackageFlightAvailability?: boolean;
    },
  ) => IFetchPackagesFlightShop;
  fetchPackagesTripDetails: (
    history: H.History,
    tripId: string,
  ) => IFetchPackagesTripDetails;
  setPackagesChosenOutgoingSlice: (
    outgoingSliceId: string,
    outgoingFareId: string,
    outgoingFareSliceId: string,
    tripId: string,
    outgoingFareRating?: number,
    resetReturnIds?: boolean,
  ) => ISetPackagesChosenOutgoingSlice;
  setPackagesChosenReturnSlice: (
    tripId: string,
    returnSliceId: string,
    returnFareId: string,
    returnFareSliceId: string,
    returnFareRating?: number,
  ) => ISetPackagesChosenReturnSlice;

  resetFilters: () => void;
  setHotelShopDateRange: (dateRange: DateRange) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
  fetchTravelWalletDetails: () => dispatch(fetchTravelWalletDetails()),
  fetchPackagesFlightShop: (
    history: H.History,
    options?: {
      overrideStateByQueryParams?: boolean;
      forceCallPackageFlightAvailability?: boolean;
    },
  ) => dispatch(fetchPackagesFlightShop(history, options)),
  fetchPackagesTripDetails: (history: H.History, tripId: string) =>
    dispatch(fetchPackagesTripDetails(history, tripId)),
  setPackagesChosenOutgoingSlice: (
    outgoingSliceId: string,
    outgoingFareId: string,
    outgoingFareSliceId: string,
    tripId: string,
    outgoingFareRating?: number,
    resetReturnIds?: boolean,
  ) =>
    dispatch(
      setPackagesChosenOutgoingSlice(
        outgoingSliceId,
        outgoingFareId,
        outgoingFareSliceId,
        tripId,
        outgoingFareRating,
        resetReturnIds,
      ),
    ),
  setPackagesChosenReturnSlice: (
    tripId: string,
    returnSliceId: string,
    returnFareId: string,
    returnFareSliceId: string,
    returnFareRating?: number,
  ) =>
    dispatch(
      setPackagesChosenReturnSlice(
        tripId,
        returnSliceId,
        returnFareId,
        returnFareSliceId,
        returnFareRating,
      ),
    ),

  resetFilters: () => {
    dispatch(setAirlineFilter(initialFilterOptions.airlineFilter));
    dispatch(setFlightNumberFilter(initialFilterOptions.flightNumberFilter));
    dispatch(setAirportFilter(initialFilterOptions.airportFilter));
    dispatch(setMaxPriceFilter(initialFilterOptions.maxPriceFilter));
    dispatch(
      setOutboundDepartureTimeRange(
        initialFilterOptions.outboundDepartureTimeRange,
      ),
    );
    dispatch(
      setOutboundArrivalTimeRange(
        initialFilterOptions.outboundArrivalTimeRange,
      ),
    );
    dispatch(
      setReturnDepartureTimeRange(
        initialFilterOptions.returnDepartureTimeRange,
      ),
    );
    dispatch(
      setReturnArrivalTimeRange(initialFilterOptions.returnArrivalTimeRange),
    );
    dispatch(setDurationFilter(initialFilterOptions.durationFilter));
    dispatch(setStopsOption(initialFilterOptions.stopsOption));
  },
  setHotelShopDateRange: (dateRange: DateRange) => {
    dispatch(setDateRange(dateRange));
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);

export type FlightListConnectorProps = ConnectedProps<typeof connector>;

export interface IFlightListProps
  extends FlightListConnectorProps,
    RouteComponentProps {}

export const ConnectedFlightList = withRouter(connector(FlightList));
